import React, { useState } from 'react';
import { Paper, Typography, Box, Avatar, IconButton, Grid, useMediaQuery, useTheme } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AttendanceTimeline from './AttendanceTimeline';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AttendanceEditModal from './AttendanceEditModal';

// ✅ Przyjęcie propsa onImageClick
function AttendanceCardComponent({ attendance, photoEntryUrl, photoExitUrl, onRefresh, onImageClick }) {

    // 📱 wykrywanie mobile (tylko do stylów/layoutu — logika biznesowa bez zmian)
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Rozwijanie komponentu dla zdjęć
    const [showCamera, setShowCamera] = useState(false);

    // Formularz edycji czasu pracy
    const [openModal, setOpenModal] = useState(false);

    if (!attendance) return null;

    // Zakres godzin paska
    const startHour = 0;
    const endHour = 24;

    // Aktualny czas
    const now = new Date();
    const currentH = now.getHours();
    const currentM = now.getMinutes();
    const currentMinutes = (currentH - startHour) * 60 + currentM;
    const currentTime = `${String(currentH).padStart(2, '0')}:${String(currentM).padStart(2, '0')}`;

    // Dane z bazy
    const workerStartString = attendance.check_in;
    const workerEndString = attendance.check_out;

    // Wyliczenie czasu pracy
    const startDate = new Date(workerStartString);
    const endDate = workerEndString !== null ? new Date(workerEndString) : null;

    const startDay = startDate.getDate();
    const endDay = endDate !== null ? endDate.getDate() : null;
    const startMonth = startDate.getMonth() + 1;
    const startYear = startDate.getFullYear();
    const endMonth = endDate !== null ? endDate.getMonth() + 1 : null;
    const endYear = endDate !== null ? endDate.getFullYear() : null;

    const startH = startDate.getHours();
    const startM = startDate.getMinutes();
    const endH = endDate !== null ? endDate.getHours() : null;
    const endM = endDate !== null ? endDate.getMinutes() : null;

    // Minuty od początku zakresu
    const workerStartMinutes = (startH - startHour) * 60 + startM;
    const workerEndMinutes = endH !== null ? (endH - startHour) * 60 + endM : null;

    // Wyliczenie czasu pracy do wyświetlenia
    const workedMinutes = workerEndMinutes !== null ? workerEndMinutes - workerStartMinutes : null;
    const workedHours = workedMinutes !== null ? Math.floor(workedMinutes / 60) : null;
    const workedRemainingMinutes = workedMinutes !== null ? workedMinutes % 60 : null;

    const currentWorkingMinutes = currentMinutes - workerStartMinutes;
    const currentWorkingHours = Math.floor(currentWorkingMinutes / 60);
    const currentRemainingMinutes = currentWorkingMinutes % 60;
    const displayHours = workedMinutes !== null ? `${workedHours}h ${workedRemainingMinutes}m` : `${currentWorkingHours}h ${currentRemainingMinutes}m`;

    // Czas do wyświetlania
    const displayStart = `${String(startH).padStart(2, '0')}:${String(startM).padStart(2, '0')}`;
    const displayEnd = workedMinutes !== null ? `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}` : 'pracuje';

    const displayStartDay = `${String(startDay)}`;
    const displayEndDay = workedMinutes !== null ? `${String(endDay)}` : 'pracuje';
    const displayStartMonth = `${String(startMonth).padStart(2, '0')}`;
    const displayEndMonth = workedMinutes !== null ? `${String(endMonth).padStart(2, '0')}` : 'pracuje';
    const displayStartYear = `${String(startYear)}`;
    const displayEndYear = workedMinutes !== null ? `${String(endYear)}` : 'pracuje';

    const displayCheckInDay = `${displayStartDay}.${displayStartMonth}.${displayStartYear}`;
    const displayCheckOutDay = workedMinutes !== null ? `${displayEndDay}.${displayEndMonth}.${displayEndYear}` : 'pracuje';

    // Zmiana typu danych do wyświetlenia w labelach
    const checkInDateValue = `${displayStartYear}-${displayStartMonth}-${displayStartDay}`;
    const checkOutDateValue = workedMinutes !== null ? `${displayEndYear}-${displayEndMonth}-${displayEndDay}` : 'pracuje';

    // Czy data rozpoczęcia i zakończenia są takie same?
    const isOvernightShift = displayCheckOutDay !== null && displayCheckInDay !== displayCheckOutDay;
    const showOvernightWarning = isOvernightShift && workerEndMinutes !== null;

    // Avatar
    const initials = `${attendance.employees.name[0]}${attendance.employees.surname[0]}`;
    const getAvatarColor = (name) => {
        const colors = ['#e57373', '#64b5f6', '#81c784', '#ffb74d', '#ba68c8', '#4db6ac', '#f06292'];
        const index = name.charCodeAt(0) % colors.length;
        return colors[index];
    };

    const avatarColor = getAvatarColor(attendance.employees.name);

    return (
        <Box>
            <Grid container spacing={isMobile ? 1 : 0}
                onClick={() => setOpenModal(true)}
                sx={{
                    justify: "center",
                    alignItems: isMobile ? 'stretch' : 'flex-start',
                    mb: '15px',
                    p: isMobile ? 1.5 : 0,
                    border: isMobile ? '1px solid rgba(0,0,0,0.08)' : 'none',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.03)',
                        transform: isMobile ? 'none' : 'scaleY(1.02)',
                        cursor: 'pointer',
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                    }
                }}>

                {/* Imię, nazwisko, status */}
                <Grid size={{ xs: 12, sm: 2 }} sx={{
                    justifyContent: "center",
                    alignItems: "center",
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: avatarColor, color: 'white', flexShrink: 0 }}>{initials}</Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant='h6'
                                    sx={{
                                        ml: 2,
                                        display: 'flex',
                                        fontWeight: '520',
                                        fontSize: isMobile ? '1rem' : undefined,
                                        whiteSpace: isMobile ? 'nowrap' : 'normal',
                                        overflow: isMobile ? 'hidden' : 'visible',
                                        textOverflow: isMobile ? 'ellipsis' : 'unset',
                                    }}>
                                    {attendance.employees.name} {attendance.employees.surname}
                                </Typography>

                                <Typography variant='h6'
                                    sx={{
                                        display: 'inline-flex',
                                        ml: 2,
                                        mt: 0.5,
                                        backgroundColor: workerEndMinutes !== null ? '#ff581b' : 'green',
                                        opacity: 0.75,
                                        color: 'white',
                                        fontWeight: '50',
                                        px: 1,
                                        borderRadius: '10px',
                                        fontSize: '0.75rem'
                                    }}>
                                    {workerEndMinutes !== null ? '• Zakończone' : '• W pracy'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                {/* Kamera / zdjęcia */}
                <Grid size={{ xs: 12, sm: 0.5 }}
                    sx={{
                        display: 'flex',
                        justifyContent: isMobile ? 'flex-start' : 'center',
                    }}>
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowCamera(!showCamera);
                        }}>
                        <CameraAltIcon />
                    </IconButton>
                    {showCamera && (
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Box onClick={(e) => {
                                e.stopPropagation();
                            }}
                                sx={{
                                    width: 80,
                                    height: 80,
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                <img
                                    src={photoEntryUrl}
                                    alt="wejście"
                                    width={80}
                                    height={80}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => onImageClick && onImageClick(photoEntryUrl)}
                                />
                            </Box>

                            <Box onClick={(e) => {
                                e.stopPropagation();
                            }}
                                sx={{
                                    width: 80,
                                    height: 80,
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                <img
                                    src={photoExitUrl}
                                    alt="wejście"
                                    width={80}
                                    height={80}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => onImageClick && onImageClick(photoExitUrl)}
                                />
                            </Box>
                        </Box>
                    )}
                </Grid>

                {/* Oś czasu */}
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box>
                        <Box sx={{ width: '100%', mb: isMobile ? 1 : 2, mt: isMobile ? 1 : 0 }}>
                            <AttendanceTimeline
                                workerStartMinutes={workerStartMinutes}
                                workerEndMinutes={workerEndMinutes}
                                displayStart={displayStart}
                                displayEnd={displayEnd}
                            />
                        </Box>
                    </Box>
                </Grid>

                {/* Godziny i daty */}
                <Grid size={{ xs: 12, sm: 1.5 }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isMobile ? 'flex-start' : 'center',
                        ml: isMobile ? 0 : 2
                    }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'flex-start' : 'center' }}>
                            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                {displayStart} → {displayEnd || "pracuje"}
                            </Typography>
                            <Typography
                                variant='body2'
                                sx={{
                                    color: showOvernightWarning ? 'red' : 'text.secondary',
                                    animation: showOvernightWarning ? 'pulse 2.0s infinite' : 'none',
                                }}>
                                {displayCheckInDay} - {displayCheckOutDay}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                {/* Przepracowany czas + edycja */}
                <Grid size={{ xs: 6, sm: 1 }}>
                    <Box>
                        <Typography variant='h6' sx={{
                            display: 'flex',
                            justifyContent: isMobile ? 'flex-start' : 'center',
                            animation: workerEndMinutes === null ? 'pulse 2.0s infinite' : 'none',
                            '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                                '100%': { opacity: 1 },
                            }
                        }}>
                            {displayHours}
                        </Typography>
                    </Box>
                </Grid>

                <Grid size={{ xs: 6, sm: 1 }} sx={{ display: 'flex', justifyContent: isMobile ? 'flex-end' : 'center' }}>
                    <IconButton onClick={() => setOpenModal(true)}>
                        <EditIcon />
                    </IconButton>
                </Grid>
            </Grid>

            <AttendanceEditModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                attendance={attendance}
                displayStart={displayStart}
                displayEnd={displayEnd}
                checkInDateValue={checkInDateValue}
                checkOutDateValue={checkOutDateValue}
                currentTime={currentTime}
                onRefresh={onRefresh}
            />
        </Box>
    );
}

export default AttendanceCardComponent;